// src/state/ThemeContext.tsx
//
// Three theme states — system / light / dark — exactly as ux-direction.md §3.3
// specifies, wired to the SAME mechanism `@govtechsg/sgds-web-component`'s own
// night.css uses: a `sgds-night-theme` class on the root element (not a custom
// `data-theme` attribute of our own), so SGDS's dark semantic tokens and ours
// switch together. Preference persists per user (C-14 — "manage layout, theme").
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

type ThemeChoice = 'system' | 'light' | 'dark'

const THEME_KEY = 'prs2.theme'
const FLAT_KEY = 'prs2.flatten'
const NIGHT_CLASS = 'sgds-night-theme'

function systemPrefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function resolveIsDark(choice: ThemeChoice): boolean {
  return choice === 'dark' || (choice === 'system' && systemPrefersDark())
}

interface ThemeState {
  readonly choice: ThemeChoice
  readonly isDark: boolean
  readonly flat: boolean
  setChoice(choice: ThemeChoice): void
  /**
   * Flip between light and dark. Two states only.
   *
   * `system` remains the INITIAL state — a user whose operating system is set to
   * dark still gets dark on first load without asking. What is gone is `system` as
   * a stop on the control: pressing it always lands on an explicit light or dark,
   * which is what the product owner asked for and is the plainer read. A control
   * whose label is a sun means "make it light", not "advance to the next of three
   * things".
   */
  toggleAppearance(): void
  toggleFlat(): void
}

const ThemeContext = createContext<ThemeState | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => {
    const stored = localStorage.getItem(THEME_KEY)
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system'
  })
  const [flat, setFlat] = useState<boolean>(() => localStorage.getItem(FLAT_KEY) === '1')
  const [isDark, setIsDark] = useState<boolean>(() => resolveIsDark(choice))

  useEffect(() => {
    const apply = () => {
      const dark = resolveIsDark(choice)
      setIsDark(dark)
      document.documentElement.classList.toggle(NIGHT_CLASS, dark)
    }
    apply()
    if (choice !== 'system') return
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    media.addEventListener('change', apply)
    return () => media.removeEventListener('change', apply)
  }, [choice])

  useEffect(() => {
    document.documentElement.toggleAttribute('data-flat', flat)
  }, [flat])

  const setChoice = useCallback((next: ThemeChoice) => {
    localStorage.setItem(THEME_KEY, next)
    setChoiceState(next)
  }, [])

  const toggleFlat = useCallback(() => {
    setFlat((prev) => {
      const next = !prev
      localStorage.setItem(FLAT_KEY, next ? '1' : '0')
      return next
    })
  }, [])

  const toggleAppearance = useCallback(() => {
    // Resolved appearance, not the stored choice — so the first press from
    // `system` lands on the opposite of what the user is actually looking at
    // rather than on whatever `system` happened to mean.
    setChoice(isDark ? 'light' : 'dark')
  }, [isDark, setChoice])

  const value = useMemo<ThemeState>(
    () => ({ choice, isDark, flat, setChoice, toggleAppearance, toggleFlat }),
    [choice, isDark, flat, setChoice, toggleAppearance, toggleFlat],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeState {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme() called outside <ThemeProvider>')
  }
  return context
}
